import { KrowCollection, KrowDocument, KrowResource } from 'store/industry'
import { ListQueryParams } from 'store/store.types'
import { api } from '../store.api'

export type TStatefulResource =
  | 'positions'
  | 'alerts'
  | 'interviews'
  | 'questionnaires'
  | 'applicants'
  | 'exports'
  | 'organization_settings'
  | 'landing_pages'

export type TStatefulEntityTypes = 'Position' | 'Applicant'

export type TStateAttributes = {
  name: string
  description: number
  entity_type: TStatefulResource
  created_at: string
  updated_at: string
}

export type TStateDocument = KrowDocument<'state', TStateAttributes>
export type TStateResource = KrowResource<'state', TStateAttributes>
export type TStateCollection = KrowCollection<'state', TStateAttributes>

export interface BulkUpdateStateParams {
  type: TStatefulResource
  entitiesIds: string[]
  alias: string
  state: string
}

const extendedApi = api.injectEndpoints({
  endpoints: (build) => ({
    listStates: build.query<TStateCollection, ListQueryParams>({
      query: (params) => {
        return {
          method: 'GET',
          url: `/states`,
          params: { ...params },
        }
      },
    }),
    currentState: build.query<TStateResource, { type: TStatefulResource; id: string }>({
      query: ({ type, id }) => {
        return {
          method: 'GET',
          url: `${type}/${id}/state`,
        }
      },
    }),
    listTransitions: build.query<TStateCollection, string>({
      query: (id) => {
        return {
          method: 'GET',
          url: `states/${id}/transitions`,
        }
      },
    }),
    updateState: build.mutation<
      TStateResource,
      { type: TStatefulResource; id: string; stateId: string; alias?: string }
    >({
      query: ({ type, id, stateId, alias }) => {
        return {
          method: 'PUT',
          url: `${type}/${id}/state/${stateId}`,
          body: { state_alias: alias },
        }
      },
    }),
    bulkUpdateState: build.mutation<unknown, BulkUpdateStateParams>({
      query: ({ type, state, entitiesIds, alias }) => {
        if (type !== 'applicants') {
          throw new Error('Unsupported entity type')
        }

        return {
          method: 'PUT',
          url: `${type}/state/${state}/bulk_update`,
          body: { entities_ids: entitiesIds, state_alias: alias },
        }
      },
      invalidatesTags: [{ type: 'Applicant', id: 'LIST' }],
    }),
  }),
})

export const {
  useListStatesQuery,
  useCurrentStateQuery,
  useListTransitionsQuery,
  useUpdateStateMutation,
  useBulkUpdateStateMutation,
} = extendedApi
